<template>
  <div :class="`bg-${background} flex column`" v-if="hasContent">
    <slot />
    <div class="row">
      <SAISpinner v-if="leftActionLoading" size="20px" class="q-ma-md" />
      <div
        v-else-if="hasLeftAction"
        :class="[
          'q-pa-md row items-center',
          { 'cursor-pointer': !disable, 'o-80': disable }
        ]"
        @click="clickAction('click:left-action')"
      >
        <QIcon
          v-if="leftActionIcon"
          :name="leftActionIcon"
          size="20px"
          class="q-mr-sm"
          color="white"
        />
        <span class="text-white text-bold">
          {{ leftActionLabel }}
        </span>
      </div>
      <QSpace />
      <div class="row">
        <SAISpinner v-if="rightActionLoading" size="20px" class="q-ma-md" />
        <div
          v-else-if="hasRightAction"
          :class="[
            'q-pa-md row items-center',
            { 'cursor-pointer': !disable, 'o-80': disable }
          ]"
          @click="clickAction('click:right-action')"
        >
          <span class="text-white text-bold">
            {{ rightActionLabel }}
          </span>
          <QIcon
            v-if="rightActionIcon"
            :name="rightActionIcon"
            :size="size"
            class="q-ml-sm q-mt-xs"
            color="white"
          />
        </div>
      </div>
    </div>
    <div
      v-if="tabs.length"
      :class="[
        'row no-wrap q-px-md q-pb-md q-col-gutter-x-md justify-between',
        {
          'q-pt-md': !hasLeftAction && !hasRightAction
        }
      ]"
    >
      <div
        class="col flex justify-center"
        :style="{ 'max-width': '280px' }"
        v-for="(tab, index) in tabs"
        :key="index"
      >
        <div
          :class="[
            'full-width rounded-borders-lg relative-position justify-center items-center',
            {
              'tab-disable o-70': isTabDisabled(tab),
              'cursor-pointer': !isTabDisabled(tab) && !tab.active,
              'q-pt-md': isMobile,
              'row no-wrap': !isMobile,
              column: isMobile
            },
            getTabBackground(tab)
          ]"
          :style="{ padding: tabPadding }"
          @click="clickTab(tab)"
        >
          <QIcon
            :class="{ 'q-mr-sm': !isMobile, 'q-mb-sm': isMobile }"
            :style="tab.iconStyle"
            :name="tab.icon"
            v-if="tab.icon"
            :size="tab.iconSize || size"
            :color="tab.active ? 'primary' : 'white'"
          />
          <p
            :style="{ 'word-spacing': wrapWords ? '100vw' : undefined }"
            :class="[
              'text-bold text-tab text-center word-break-word',
              {
                'text-white': !tab.active,
                'text-grey-8': tab.active,
                'q-ml-xs': !isMobile
              }
            ]"
          >
            {{ tab.label }}
          </p>
          <QIcon
            v-if="tab.checked"
            class="checked-icon"
            size="20px"
            :name="tab.active ? checkedIconActive : checkedIcon"
            :color="tab.active ? 'primary' : 'white'"
          />
        </div>
      </div>
    </div>
    <div class="inset-border" v-if="!hideInsetBackground" />
  </div>
</template>

<script>
import { QIcon, QSpace } from 'quasar';
import { SAISpinner } from '../../../../index';

export default {
  name: 'SaiTabsHeader',
  props: {
    /**
     * A tab consists of optional fields:
     *
     * active Boolean - True if the tab should display in a white background active state.
     * disable Boolean - True if the tab should not be interactable. Changes the checked badge to $primary-semi-dark.
     * label String - Text to display on a button when it is active.
     * icon String - An image to be rendered by a QIcon component. The active state determines the color.
     * iconSize String - An override of the default component size
     * checked Boolean - True if a QBadge should display on the tab when not active. Defaults to eva-checkmark-outline.
     */
    tabs: {
      type: Array,
      default: () => []
    },
    background: {
      type: String,
      default: 'primary-banner-gradient'
    },
    hideInsetBackground: Boolean,
    size: {
      type: String,
      default: '24px'
    },
    checkedIcon: {
      type: String,
      default: 'eva-checkmark-circle-2'
    },
    checkedIconActive: {
      type: String,
      default: 'eva-checkmark-circle-2-outline'
    },
    leftActionIcon: {
      type: String,
      default: 'eva-arrow-back-outline'
    },
    leftActionLabel: String,
    leftActionLoading: Boolean,
    rightActionIcon: String,
    rightActionLabel: String,
    rightActionLoading: Boolean,
    disable: Boolean,
    isMobile: Boolean,
    wrapWords: Boolean,
    tabPadding: {
      type: String,
      default: '8px'
    }
  },
  computed: {
    hasLeftAction() {
      return this.$listeners['click:left-action'] && this.leftActionLabel;
    },
    hasRightAction() {
      return this.$listeners['click:right-action'] && this.rightActionLabel;
    },
    hasSlot() {
      return this.$slots.default;
    },
    hasContent() {
      return Boolean(
        this.hasSlot ||
          this.hasLeftAction ||
          this.hasRightAction ||
          (this.tabs && this.tabs.length)
      );
    }
  },
  methods: {
    isTabDisabled(tab) {
      return this.disable || tab.disable;
    },
    getTabBackground(tab) {
      if (tab.active) {
        return 'bg-white';
      }
      return this.isTabDisabled(tab)
        ? 'bg-primary-semi-light'
        : 'bg-primary-semi-dark';
    },
    clickAction(action) {
      if (!this.disable) {
        this.$emit(action);
      }
    },
    clickTab(tab) {
      if (!this.isTabDisabled(tab)) {
        this.$emit('input', tab);
      }
    }
  },
  components: {
    QIcon,
    QSpace,
    SAISpinner
  }
};
</script>

<style lang="sass" scoped>
.checked-icon
  position: absolute
  top: 6px
  right: 6px
  bottom: initial

.inset-border
  height: 25px
  width: 100%
  background: white
  border-top-left-radius: 24px
  border-top-right-radius: 24px
  position: relative
  bottom: -1px
</style>
